import { GasPrices } from '@darkforest_eth/types';

/**
 * Get gas prices from local Netlify function
 * Returns null if API is unavailable to indicate failure
 */
export async function getLocalGasPrices(): Promise<GasPrices | null> {
  try {
    const response = await fetch('/.netlify/functions/gas-price', {
      headers: { 'Accept': 'application/json' },
    });

    if (response.ok) {
      const data = await response.json();

      // Check for error response
      if (data.error) {
        console.warn('Gas price API error:', data.error);
        return null;
      }

      // Return data if valid
      if (data.slow && data.average && data.fast) {
        return {
          slow: data.slow,
          average: data.average,
          fast: data.fast,
        };
      }
    }
  } catch (error) {
    console.warn('Failed to fetch gas prices:', error);
  }

  return null;
}

/**
 * Create gas prices stream to replace ethConnection.gasPrices$
 * Returns null for gas prices when API is unavailable
 */
export function createLocalGasPricesStream(): {
  gasPrices$: { subscribe: (callback: (prices: GasPrices | null) => void) => void };
  getAutoGasPrices: () => Promise<GasPrices | null>;
} {
  let currentPrices: GasPrices | null = null;
  const subscribers: ((prices: GasPrices | null) => void)[] = [];

  const updatePrices = async () => {
    const newPrices = await getLocalGasPrices();
    currentPrices = newPrices;
    subscribers.forEach(callback => callback(newPrices));
  };

  // Update immediately, then every 60 seconds
  updatePrices();
  setInterval(updatePrices, 60000);

  return {
    gasPrices$: {
      subscribe: (callback: (prices: GasPrices | null) => void) => {
        subscribers.push(callback);
        callback(currentPrices); // Return current prices immediately

        return () => {
          const index = subscribers.indexOf(callback);
          if (index > -1) subscribers.splice(index, 1);
        };
      }
    },
    getAutoGasPrices: () => getLocalGasPrices()
  };
}
